/**
 * Local library album index — lightweight catalog rows for the album wall.
 *
 * Libraries ≤ ALBUM_INDEX_BULK_CAP load fully (bulk or paginated) for byte-identical
 * small-library behavior. Larger libraries enter windowed mode: first chunk paints,
 * additional pages load on scroll via loadNextLocalAlbumIndexPage (#417).
 */
import { libraryService } from './libraryService';
import { shouldUseLocalLibraryIpc, isLocalCatalogBrowseReady } from './localLibraryIpcGate';
import { isLibraryScanInProgress } from './libraryScanCoordinator';
import {
  callWithLibraryBusyRetry,
  isGetAlbumsTimeoutError,
  isLibraryBusyScanningError,
  isLibraryCatalogRetryableError,
} from '../utils/libraryBusyScanning';
import { awaitCleanScanStatusAfterWebviewRecovery } from '../utils/libraryIndexReloadGate';
import {
  extractCatalogBootEnvelope,
  type CatalogBootEnvelope,
} from './catalogBootSnapshot';

/** First paint chunk — small enough to return while a background scan is running. */
export const ALBUM_INDEX_FIRST_CHUNK = 200;
/** Legacy alias used by tests and callers expecting a single chunk constant. */
export const ALBUM_INDEX_CHUNK_SIZE = ALBUM_INDEX_FIRST_CHUNK;
/** Libraries at or below this size load in one IPC round-trip (faster than multi-chunk on busy hosts). */
export const ALBUM_INDEX_BULK_CAP = 5000;

/** Client-side retries per chunk when get_albums times out or errors (#366 scan-aware backoff). */
export const LOCAL_LIBRARY_GET_ALBUMS_MAX_RETRIES = 3;
export const LOCAL_LIBRARY_GET_ALBUMS_RETRY_BACKOFF_MS = [2000, 4000, 8000] as const;
/** visibility gap that suggests sleep/wake — trigger a fresh index load. */
const WAKE_RESUME_GAP_MS = 30_000;

export type AlbumIndexRow = Record<string, unknown>;

export type AlbumIndexLoadResult = {
  albums: AlbumIndexRow[];
  total: number;
  bytes: number;
  ms: number;
  complete: boolean;
  error?: string | null;
};

type ChunkHandler = (snapshot: {
  albums: AlbumIndexRow[];
  total: number;
  totalReleases: number;
  bytes: number;
  complete: boolean;
  /** Boot snapshot envelope from latest get_albums page (R-B). */
  catalogBoot?: CatalogBootEnvelope;
}) => void;

/** Opportunistic boot-surface upgrade only — never gates index load. */
function deliverIndexChunk(
  handler: ChunkHandler | undefined,
  snapshot: Parameters<ChunkHandler>[0],
): void {
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('maestro:boot-album-progress', {
        detail: {
          count: snapshot.albums?.length ?? 0,
          total: snapshot.total ?? 0,
          complete: !!snapshot.complete,
        },
      }));
    } catch {
      /* presentational */
    }
  }
  handler?.(snapshot);
}

type DedupeResult = {
  albums: AlbumIndexRow[];
  collapsed: number;
};

let activeGeneration = 0;
let activeAbort: AbortController | null = null;
/** Completed index load cycles — field log proof the refetch loop is gone. */
let indexLoadCycles = 0;
let inFlightLoad: {
  generation: number;
  promise: Promise<AlbumIndexLoadResult>;
  chunkHandlers: Set<ChunkHandler>;
} | null = null;
let indexComplete = false;
/** Large libraries (> ALBUM_INDEX_BULK_CAP): first chunk only until scroll/on-demand pages. */
let indexWindowed = false;
let indexSort = 'artist';
let pageLoadInFlight: Promise<boolean> | null = null;
let indexAlbums: AlbumIndexRow[] = [];
let indexTotal = 0;
let indexTotalReleases = 0;
let indexBytes = 0;
/** Catalog version + album count the in-memory index was built from (Connector IPC). */
let indexCatalogVersion: number | null = null;
let indexAlbumCount: number | null = null;
/** Sorted album ids + title + cover hashes — grid-relevant fingerprint, not raw catalogVersion. */
let indexGridFingerprint: string | null = null;
let indexLoadError: string | null = null;
const indexLoadErrorListeners = new Set<() => void>();

function notifyIndexLoadErrorListeners(): void {
  indexLoadErrorListeners.forEach((cb) => cb());
}

function setIndexLoadError(message: string | null): void {
  indexLoadError = message;
  notifyIndexLoadErrorListeners();
}

export function getLocalAlbumIndexLoadError(): string | null {
  return indexLoadError;
}

export function clearLocalAlbumIndexLoadError(): void {
  setIndexLoadError(null);
}

export function subscribeLocalAlbumIndexLoadError(cb: () => void): () => void {
  indexLoadErrorListeners.add(cb);
  return () => indexLoadErrorListeners.delete(cb);
}

let wakeRecoveryInstalled = false;

/** After a long hidden gap (sleep/wake), cancel any stuck load and start fresh. */
export function installLocalAlbumIndexWakeRecovery(onResume: () => void): void {
  if (wakeRecoveryInstalled || typeof document === 'undefined') return;
  wakeRecoveryInstalled = true;
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    if (document.visibilityState !== 'visible' || hiddenAt <= 0) return;
    const gapMs = Date.now() - hiddenAt;
    hiddenAt = 0;
    if (gapMs < WAKE_RESUME_GAP_MS) return;
    console.info(
      `[library-grid] app resumed after ${Math.round(gapMs / 1000)}s — restarting album index load`,
    );
    cancelLocalAlbumIndexLoad();
    clearLocalAlbumIndexLoadError();
    onResume();
  });
}

export type LocalAlbumIndexSessionMeta = {
  catalogVersion: number | null;
  albumCount: number | null;
  albumReleaseCount: number | null;
  gridFingerprint: string | null;
  bytes: number;
  complete: boolean;
};

/** Grid-visible slice of one album row (id, title, cover hashes). */
export function albumRowGridSlice(a: AlbumIndexRow | null | undefined): string {
  if (!a) return '';
  const id = String(a.album_key ?? a.id ?? '').trim();
  const title = String(a.title ?? a.album ?? '').trim();
  const embedded = String(a.embedded_cover_hash ?? '').trim();
  const folder = String(a.folder_cover_hash ?? '').trim();
  return `${id}\u001f${title}\u001f${embedded}\u001f${folder}`;
}

export function computeAlbumGridFingerprint(albums: AlbumIndexRow[]): string {
  if (!albums?.length) return '0';
  const slices = albums.map((a) => albumRowGridSlice(a)).sort();
  return `${albums.length}:${slices.join('\u001e')}`;
}

/** Compare warm index fingerprint to a fresh catalog slice — detects id drift at stable counts. */
export function gridFingerprintDrifted(
  warmFingerprint: string | null | undefined,
  freshAlbums: AlbumIndexRow[],
): boolean {
  if (!warmFingerprint || !freshAlbums?.length) return false;
  return computeAlbumGridFingerprint(freshAlbums) !== warmFingerprint;
}

function refreshStoredGridFingerprint(): void {
  indexGridFingerprint = indexAlbums.length ? computeAlbumGridFingerprint(indexAlbums) : null;
}

type DiffEntityItem = {
  entity_type?: string;
  entityType?: string;
  id?: string;
};

function diffEntityType(item: DiffEntityItem): string {
  return String(item.entity_type ?? item.entityType ?? '').trim();
}

export function isLocalAlbumIndexWarm(): boolean {
  return shouldUseLocalLibraryIpc() && indexComplete && indexAlbums.length > 0;
}

export function getLocalAlbumIndexSessionMeta(): LocalAlbumIndexSessionMeta {
  return {
    catalogVersion: indexCatalogVersion,
    albumCount: indexAlbumCount,
    albumReleaseCount: indexTotalReleases || null,
    gridFingerprint: indexGridFingerprint,
    bytes: indexBytes,
    complete: indexComplete,
  };
}

export function setLocalAlbumIndexSessionMeta(
  catalogVersion: number | null,
  albumCount: number | null,
): void {
  if (catalogVersion != null && Number.isFinite(catalogVersion)) {
    indexCatalogVersion = catalogVersion;
  }
  if (albumCount != null && Number.isFinite(albumCount)) {
    indexAlbumCount = albumCount;
  }
}

function normalizeTitle(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function normalizeArtist(value: unknown): string {
  let s = normalizeTitle(value);
  if (s.startsWith('the ')) s = s.slice(4);
  return s;
}

/** Canonical identity for index dedupe — stable id when present, else title+artist+year. */
export function albumIdentityKey(a: AlbumIndexRow | null | undefined): string {
  if (!a) return '';
  const stable = String(a.album_key ?? a.id ?? '').trim();
  if (stable) return stable;
  const title = normalizeTitle(a.title ?? a.album);
  if (!title) return '';
  const artist = normalizeArtist(a.artist_name ?? a.album_artist ?? a.artist);
  const year = a.year != null && a.year !== '' ? String(a.year) : '';
  return `${artist}\u001f${title}\u001f${year}`;
}

function albumRowScore(a: AlbumIndexRow): number {
  let score = Number(a.track_count) || 0;
  if (a.embedded_cover_hash || a.folder_cover_hash) score += 1000;
  if (a.cover_art_url && String(a.cover_art_url).trim()) score += 500;
  if (a.track_count && Number(a.track_count) > 0) score += Number(a.track_count);
  return score;
}

/**
 * Collapse duplicate catalog rows to one entry per albumIdentityKey.
 * Preserves first-seen order; when duplicates share an identity, keeps the richer row.
 */
export function dedupeAlbumIndexRows(albums: AlbumIndexRow[]): DedupeResult {
  if (!albums?.length) return { albums: albums ?? [], collapsed: 0 };

  const order: string[] = [];
  const byKey = new Map<string, AlbumIndexRow>();
  let collapsed = 0;

  for (let i = 0; i < albums.length; i += 1) {
    const row = albums[i];
    if (!row) continue;
    let key = albumIdentityKey(row);
    if (!key) key = `__row_${i}`;

    const prev = byKey.get(key);
    if (prev) {
      collapsed += 1;
      if (albumRowScore(row) >= albumRowScore(prev)) {
        byKey.set(key, { ...prev, ...row });
      }
      continue;
    }
    order.push(key);
    byKey.set(key, row);
  }

  return {
    albums: order.map((key) => byKey.get(key)!),
    collapsed,
  };
}

/** React list key — identity when unique; index suffix only as last resort. */
export function getAlbumGridRowKey(album: AlbumIndexRow, listIndex: number): string {
  const identity = albumIdentityKey(album);
  return identity || `album-row-${listIndex}`;
}

function reconcileAlbumsFromServer(prev: AlbumIndexRow[], incoming: AlbumIndexRow[]): AlbumIndexRow[] {
  if (!incoming?.length) return incoming ?? [];
  if (!prev?.length) return incoming;
  const prevByKey = new Map<string, AlbumIndexRow>();
  for (const a of prev) {
    const k = albumIdentityKey(a);
    if (k) prevByKey.set(k, a);
  }
  return incoming.map((a) => {
    const k = albumIdentityKey(a);
    const prior = k ? prevByKey.get(k) : null;
    return prior ? { ...prior, ...a } : a;
  });
}

function applyChunkDedupe(raw: AlbumIndexRow[]): DedupeResult {
  return dedupeAlbumIndexRows(raw);
}

function logBornToRunIdentityCounts(albums: AlbumIndexRow[]): void {
  const born = albums.filter((a) => normalizeTitle(a?.title ?? a?.album).includes('born to run'));
  if (born.length === 0) return;
  const byIdentity = new Map<string, number>();
  for (const row of born) {
    const key = albumIdentityKey(row) || '(empty)';
    byIdentity.set(key, (byIdentity.get(key) || 0) + 1);
  }
  const shared = [...byIdentity.entries()].filter(([, count]) => count > 1);
  if (shared.length === 0) return;
  console.log('[library-grid] born-to-run identity collision', {
    rows: born.length,
    identities: byIdentity.size,
    shared: Object.fromEntries(shared),
  });
}

function estimatePayloadBytes(albums: AlbumIndexRow[]): number {
  try {
    return new TextEncoder().encode(JSON.stringify(albums)).length;
  } catch {
    return albums.length * 400;
  }
}

export function isLocalAlbumIndexComplete(): boolean {
  return indexComplete;
}

export function isLocalAlbumIndexWindowed(): boolean {
  return indexWindowed;
}

export function isLocalAlbumIndexFullyLoaded(): boolean {
  return indexComplete && (!indexWindowed || indexAlbums.length >= indexTotal);
}

export function hasMoreLocalAlbumIndexPages(): boolean {
  if (!shouldUseLocalLibraryIpc()) return false;
  if (indexTotal <= 0) return false;
  return indexWindowed && indexAlbums.length < indexTotal;
}

export function getLocalAlbumIndexSnapshot(): {
  albums: AlbumIndexRow[];
  total: number;
  totalReleases: number;
  bytes: number;
} {
  return {
    albums: indexAlbums,
    total: indexTotal,
    totalReleases: indexTotalReleases,
    bytes: indexBytes,
  };
}

/** Look up a loaded index row by album_key / id (bare hash or local:album: URI). */
export function findLocalAlbumIndexRow(albumKey?: string | null): AlbumIndexRow | null {
  const raw = String(albumKey || '').trim();
  if (!raw) return null;
  const bare = raw.replace(/^local:album:/i, '').toLowerCase();
  for (const row of indexAlbums) {
    const key = String(row.album_key ?? row.id ?? '').trim();
    if (!key) continue;
    const rowBare = key.replace(/^local:album:/i, '').toLowerCase();
    if (rowBare === bare || key === raw) return row;
  }
  return null;
}

/** Drop one stale/ghost tile from the in-memory index without clearing scroll prefs. */
export function removeAlbumFromLocalIndex(albumKey?: string | null): boolean {
  const raw = String(albumKey || '').trim();
  if (!raw) return false;
  const bare = raw.replace(/^local:album:/i, '').toLowerCase();
  const before = indexAlbums.length;
  indexAlbums = indexAlbums.filter((row) => {
    const key = String(row.album_key ?? row.id ?? '').trim();
    if (!key) return true;
    const rowBare = key.replace(/^local:album:/i, '').toLowerCase();
    return rowBare !== bare && key !== raw;
  });
  if (indexAlbums.length === before) return false;
  indexTotal = Math.max(indexAlbums.length, indexTotal - 1);
  indexAlbumCount = indexAlbums.length;
  indexBytes = estimatePayloadBytes(indexAlbums);
  refreshStoredGridFingerprint();
  return true;
}

export function getLocalAlbumIndexLoadCycles(): number {
  return indexLoadCycles;
}

export function resetLocalAlbumIndex(): void {
  activeGeneration += 1;
  activeAbort?.abort();
  activeAbort = null;
  inFlightLoad = null;
  pageLoadInFlight = null;
  indexComplete = false;
  indexWindowed = false;
  indexSort = 'artist';
  indexAlbums = [];
  indexTotal = 0;
  indexTotalReleases = 0;
  indexBytes = 0;
  indexCatalogVersion = null;
  indexAlbumCount = null;
  indexGridFingerprint = null;
}

/** Destructive catalog wipe/remove — cold grid refresh (mirrors wipe + scan-started invalidation). */
export function dispatchLocalAlbumCatalogCleared(): void {
  resetLocalAlbumIndex();
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('maestro:library:changed', {
      detail: { forceFullRefresh: true, reason: 'catalog-cleared' },
    }));
  } catch {
    /* ignore */
  }
}

export function cancelLocalAlbumIndexLoad(): void {
  activeGeneration += 1;
  activeAbort?.abort();
  activeAbort = null;
  inFlightLoad = null;
}

function deliverIndexChunkToHandlers(
  handlers: Set<ChunkHandler>,
  snapshot: Parameters<ChunkHandler>[0],
): void {
  for (const handler of handlers) {
    deliverIndexChunk(handler, snapshot);
  }
}

function bulkCapLibrarySettled(total: number, fetchedAll: boolean, useBulkFetch: boolean): boolean {
  return useBulkFetch
    && fetchedAll
    && total > 0
    && total <= ALBUM_INDEX_BULK_CAP;
}

type AlbumListFetchArgs = {
  sort: string;
  limit: number;
  offset: number;
};

async function fetchAlbumsPageWithRetry(
  args: AlbumListFetchArgs,
  signal: AbortSignal,
): Promise<Awaited<ReturnType<typeof libraryService.getAlbums>>> {
  return callWithLibraryBusyRetry(
    () => libraryService.getAlbums(args),
    { signal },
  );
}

function mergeIndexPage(
  page: AlbumIndexRow[],
  offset: number,
): { albums: AlbumIndexRow[]; collapsed: number } {
  let accumulated: AlbumIndexRow[];
  if (offset === 0) {
    accumulated = page.slice();
  } else {
    accumulated = indexAlbums.concat(page);
  }
  const { albums, collapsed } = applyChunkDedupe(accumulated);
  indexAlbums = albums;
  indexBytes = estimatePayloadBytes(albums);
  return { albums, collapsed };
}

/**
 * Fetch the next windowed page (limit=200). No-op when the index is fully loaded
 * or the library is below the windowed threshold.
 */
export async function loadNextLocalAlbumIndexPage(options: {
  sort?: string;
  onChunk?: ChunkHandler;
} = {}): Promise<boolean> {
  if (!shouldUseLocalLibraryIpc() || !hasMoreLocalAlbumIndexPages()) return false;

  if (pageLoadInFlight) {
    await pageLoadInFlight;
    return hasMoreLocalAlbumIndexPages();
  }

  const sort = options.sort ?? indexSort ?? 'artist';
  const offset = indexAlbums.length;

  pageLoadInFlight = (async () => {
    if (!activeAbort || activeAbort.signal.aborted) {
      activeAbort = new AbortController();
    }
    const { signal } = activeAbort;

    try {
      const resp = await fetchAlbumsPageWithRetry({
        sort,
        limit: ALBUM_INDEX_FIRST_CHUNK,
        offset,
      }, signal);
      if (signal.aborted) return false;

      const page = (resp?.albums || []) as AlbumIndexRow[];
      const catalogBoot = extractCatalogBootEnvelope(
        resp as Record<string, unknown> | null | undefined,
      );
      const pageTotal = Number(resp?.total_count ?? resp?.total ?? indexTotal ?? page.length);
      if (pageTotal > 0) indexTotal = pageTotal;
      const pageReleases = Number(
        (resp as { total_releases?: number })?.total_releases ?? 0,
      );
      if (Number.isFinite(pageReleases) && pageReleases > 0) {
        indexTotalReleases = pageReleases;
      }

      const { albums } = mergeIndexPage(page, offset);
      const complete = albums.length >= indexTotal || page.length === 0;

      deliverIndexChunk(options.onChunk, {
        albums,
        total: indexTotal,
        totalReleases: indexTotalReleases,
        bytes: indexBytes,
        complete,
        catalogBoot,
      });

      if (complete) {
        indexComplete = true;
        refreshStoredGridFingerprint();
      }

      return hasMoreLocalAlbumIndexPages();
    } catch (e) {
      if (!signal.aborted && !isLibraryCatalogRetryableError(e)) {
        console.warn('[library-grid] windowed page load failed:', e);
      }
      return hasMoreLocalAlbumIndexPages();
    } finally {
      pageLoadInFlight = null;
    }
  })();

  return pageLoadInFlight;
}

/**
 * Load the full local album index in large background chunks. Safe to call
 * multiple times — concurrent callers join the in-flight load (single-flight).
 * Explicit resetLocalAlbumIndex / cancelLocalAlbumIndexLoad start fresh.
 */
export async function loadLocalAlbumIndex(options: {
  sort?: string;
  onChunk?: ChunkHandler;
  /** When true, reconcile the first chunk against existing rows (scan-time refresh). */
  mergeFirstChunk?: boolean;
  existing?: AlbumIndexRow[];
} = {}): Promise<AlbumIndexLoadResult> {
  if (!shouldUseLocalLibraryIpc()) {
    return { albums: [], total: 0, bytes: 0, ms: 0, complete: true };
  }

  if (
    inFlightLoad
    && inFlightLoad.generation === activeGeneration
  ) {
    if (options.onChunk) inFlightLoad.chunkHandlers.add(options.onChunk);
    return inFlightLoad.promise;
  }

  const generation = activeGeneration;
  const chunkHandlers = new Set<ChunkHandler>();
  if (options.onChunk) chunkHandlers.add(options.onChunk);

  const promise = executeLocalAlbumIndexLoad(options, generation, chunkHandlers);
  inFlightLoad = { generation, promise, chunkHandlers };

  try {
    return await promise;
  } finally {
    if (inFlightLoad?.promise === promise) inFlightLoad = null;
  }
}

async function executeLocalAlbumIndexLoad(
  options: {
    sort?: string;
    mergeFirstChunk?: boolean;
    existing?: AlbumIndexRow[];
  },
  generation: number,
  chunkHandlers: Set<ChunkHandler>,
): Promise<AlbumIndexLoadResult> {
  const started = performance.now();
  const sort = options.sort ?? 'artist';

  if (!activeAbort || activeAbort.signal.aborted) {
    activeAbort = new AbortController();
  }
  const { signal } = activeAbort;

  setIndexLoadError(null);

  const recoveryGate = await awaitCleanScanStatusAfterWebviewRecovery(signal);
  if (recoveryGate === 'defer_scan_active') {
    if (signal.aborted || generation !== activeGeneration) {
      return {
        albums: indexAlbums,
        total: indexTotal,
        bytes: indexBytes,
        ms: Math.round(performance.now() - started),
        complete: false,
      };
    }
    deliverIndexChunkToHandlers(chunkHandlers, {
      albums: indexAlbums.length ? indexAlbums : (options.existing ?? []),
      total: indexTotal,
      totalReleases: indexTotalReleases,
      bytes: indexBytes,
      complete: false,
    });
    return {
      albums: indexAlbums,
      total: indexTotal,
      bytes: indexBytes,
      ms: Math.round(performance.now() - started),
      complete: false,
    };
  }

  const existingDeduped = options.existing?.length
    ? dedupeAlbumIndexRows(options.existing).albums
    : undefined;

  if (!options.mergeFirstChunk) {
    indexComplete = false;
    indexWindowed = false;
  }

  indexSort = sort;
  let accumulated: AlbumIndexRow[] = [];
  let serverTotal = 0;
  let offset = 0;
  let totalCollapsed = 0;
  let lastRawBeforeDedupe = 0;
  let lastRawAlbums: AlbumIndexRow[] = [];
  let useBulkFetch = false;
  let loadError: string | null = null;
  let bulkSettled = false;

  try {
    for (;;) {
      if (signal.aborted || generation !== activeGeneration) break;

      const pageLimit = offset === 0 && !useBulkFetch
        ? ALBUM_INDEX_FIRST_CHUNK
        : (useBulkFetch ? serverTotal : ALBUM_INDEX_FIRST_CHUNK);

      const resp = await fetchAlbumsPageWithRetry({
        sort,
        limit: pageLimit,
        offset: useBulkFetch ? 0 : offset,
      }, signal);
      if (signal.aborted || generation !== activeGeneration) break;

      const page = (resp?.albums || []) as AlbumIndexRow[];
      const catalogBoot = extractCatalogBootEnvelope(
        resp as Record<string, unknown> | null | undefined,
      );
      if (offset === 0 || useBulkFetch) {
        const pageVersion = Number((resp as { version?: number })?.version ?? 0);
        if (pageVersion > 0) indexCatalogVersion = pageVersion;
      }
      serverTotal = Number(resp?.total_count ?? resp?.total ?? serverTotal ?? page.length);
      const pageReleases = Number(
        (resp as { total_releases?: number })?.total_releases ?? 0,
      );
      if (Number.isFinite(pageReleases) && pageReleases > 0) {
        indexTotalReleases = pageReleases;
      }

      if (useBulkFetch) {
        accumulated = page.slice();
      } else if (offset === 0 && options.mergeFirstChunk && existingDeduped?.length) {
        accumulated = reconcileAlbumsFromServer(existingDeduped, page);
      } else if (offset === 0) {
        accumulated = page.slice();
      } else {
        accumulated = accumulated.concat(page);
      }

      lastRawBeforeDedupe = accumulated.length;
      lastRawAlbums = accumulated;
      const { albums: deduped, collapsed } = applyChunkDedupe(accumulated);
      totalCollapsed += collapsed;
      accumulated = deduped;

      indexAlbums = accumulated;
      indexTotal = serverTotal || accumulated.length;
      indexBytes = estimatePayloadBytes(accumulated);

      const fetchedAll = page.length === 0
        || accumulated.length >= indexTotal
        || (useBulkFetch && page.length >= indexTotal);

      if (fetchedAll && indexTotal === 0 && indexAlbums.length === 0 && shouldUseLocalLibraryIpc()) {
        if (!isLocalCatalogBrowseReady()) {
          deliverIndexChunkToHandlers(chunkHandlers, {
            albums: accumulated,
            total: indexTotal,
            totalReleases: indexTotalReleases,
            bytes: indexBytes,
            complete: false,
            catalogBoot,
          });
          break;
        }
        try {
          const stats = await libraryService.getStats();
          const statsAlbums = Number(stats?.albums ?? stats?.total_albums ?? -1);
          if (Number.isFinite(statsAlbums) && statsAlbums > 0) {
            deliverIndexChunkToHandlers(chunkHandlers, {
              albums: accumulated,
              total: indexTotal,
              totalReleases: indexTotalReleases,
              bytes: indexBytes,
              complete: false,
              catalogBoot,
            });
            break;
          }
        } catch {
          deliverIndexChunkToHandlers(chunkHandlers, {
            albums: accumulated,
            total: indexTotal,
            totalReleases: indexTotalReleases,
            bytes: indexBytes,
            complete: false,
            catalogBoot,
          });
          break;
        }
      }

      if (!useBulkFetch && offset === 0 && indexTotal > page.length && indexTotal <= ALBUM_INDEX_BULK_CAP) {
        useBulkFetch = true;
        deliverIndexChunkToHandlers(chunkHandlers, {
          albums: accumulated,
          total: indexTotal,
          totalReleases: indexTotalReleases,
          bytes: indexBytes,
          complete: false,
          catalogBoot,
        });
        continue;
      }

      // #417 — large libraries: first chunk paints; scroll/on-demand loads the rest.
      if (
        !useBulkFetch
        && offset === 0
        && indexTotal > ALBUM_INDEX_BULK_CAP
        && indexTotal > page.length
      ) {
        indexWindowed = true;
        indexComplete = true;
        deliverIndexChunkToHandlers(chunkHandlers, {
          albums: accumulated,
          total: indexTotal,
          totalReleases: indexTotalReleases,
          bytes: indexBytes,
          complete: true,
          catalogBoot,
        });
        console.log(
          `[library-grid] index windowed n=${accumulated.length} total=${indexTotal} — scroll loads pages`,
        );
        break;
      }

      let complete = fetchedAll
        || (!useBulkFetch && page.length < ALBUM_INDEX_FIRST_CHUNK);
      if (bulkCapLibrarySettled(indexTotal, fetchedAll, useBulkFetch)) {
        complete = true;
        indexComplete = true;
        bulkSettled = true;
      }
      const deferForActiveScan = complete
        && indexTotal === 0
        && indexAlbums.length === 0
        && isLibraryScanInProgress();
      if (deferForActiveScan) {
        complete = false;
        indexComplete = false;
        bulkSettled = false;
      }

      deliverIndexChunkToHandlers(chunkHandlers, {
        albums: accumulated,
        total: indexTotal,
        totalReleases: indexTotalReleases,
        bytes: indexBytes,
        complete,
        catalogBoot,
      });

      if (complete) {
        indexComplete = true;
        break;
      }

      // Empty page or scan deferral — exit; scan poll (≤2s) will re-invoke.
      if (page.length === 0 || deferForActiveScan) {
        break;
      }

      offset += page.length;
    }
  } catch (e) {
    if (!signal.aborted) {
      const message = e instanceof Error ? e.message : String(e);
      if (!isLibraryCatalogRetryableError(e)) {
        loadError = message || 'Library catalog load failed';
        setIndexLoadError(loadError);
        console.warn('[library-grid] index load failed:', e);
      } else if (isGetAlbumsTimeoutError(e)) {
        console.info('[library-grid] index load deferred — get_albums timeout (scan-aware backoff)');
      }
      deliverIndexChunkToHandlers(chunkHandlers, {
        albums: indexAlbums.length ? indexAlbums : accumulated,
        total: indexTotal || serverTotal || indexAlbums.length,
        totalReleases: indexTotalReleases,
        bytes: indexBytes,
        complete: false,
      });
    }
  }

  const ms = Math.round(performance.now() - started);
  const complete = indexComplete && generation === activeGeneration && !loadError;

  if (complete) {
    indexAlbumCount = indexTotal || indexAlbums.length;
    refreshStoredGridFingerprint();
    indexLoadCycles += 1;
    const rawCount = lastRawBeforeDedupe || indexAlbums.length + totalCollapsed;
    console.log(
      `[library-grid] index loaded n=${rawCount} unique=${indexAlbums.length} bytes=${indexBytes} ms=${ms} catalogVersion=${indexCatalogVersion ?? 'n/a'} fingerprint=${indexGridFingerprint ?? 'n/a'}`,
    );
    if (bulkSettled || (indexTotal > 0 && indexTotal <= ALBUM_INDEX_BULK_CAP && indexComplete)) {
      console.log(`[library-grid] index settled n=${indexAlbums.length} loads=${indexLoadCycles}`);
    }
    if (totalCollapsed > 0) {
      console.log(`[library-grid] index dedupe collapsed=${totalCollapsed} duplicate rows`);
      logBornToRunIdentityCounts(lastRawAlbums);
    }
  }

  return {
    albums: indexAlbums,
    total: indexTotal,
    bytes: indexBytes,
    ms,
    complete,
    error: loadError,
  };
}

/** Wait until the in-memory index contains at least `targetCount` rows (scroll restore). */
export async function waitForLocalAlbumIndexDepth(
  targetCount: number,
  options: { sort?: string; onChunk?: ChunkHandler; existing?: AlbumIndexRow[] } = {},
): Promise<number> {
  const target = Math.max(0, Number(targetCount) || 0);
  if (!shouldUseLocalLibraryIpc()) return indexAlbums.length;
  if (indexAlbums.length >= target && isLocalAlbumIndexFullyLoaded()) return indexAlbums.length;
  if (indexAlbums.length >= target && indexWindowed) return indexAlbums.length;

  const existing = options.existing?.length
    ? dedupeAlbumIndexRows(options.existing).albums
    : dedupeAlbumIndexRows(indexAlbums).albums;

  if (!indexWindowed && !indexComplete) {
    await loadLocalAlbumIndex({
      sort: options.sort,
      onChunk: options.onChunk,
      mergeFirstChunk: !!existing.length,
      existing,
    });
    if (indexAlbums.length >= target || isLocalAlbumIndexFullyLoaded()) {
      return indexAlbums.length;
    }
  }

  while (indexAlbums.length < target && hasMoreLocalAlbumIndexPages()) {
    await loadNextLocalAlbumIndexPage({
      sort: options.sort,
      onChunk: options.onChunk,
    });
  }

  return indexAlbums.length;
}

/** Dedupe scroll-restore or other hydrated rows before they enter React state. */
export function dedupeAlbumRowsForGrid(albums: AlbumIndexRow[]): AlbumIndexRow[] {
  return dedupeAlbumIndexRows(albums).albums;
}

export type LocalAlbumSessionRevalidateResult = {
  status: 'unchanged' | 'merged' | 'reloaded';
  reason: string;
};

async function mergeGridRelevantAlbumUpdates(
  items: DiffEntityItem[],
  existing: AlbumIndexRow[],
): Promise<{ changed: boolean; albums: AlbumIndexRow[]; updatedCount: number }> {
  let albums = existing;
  let changed = false;
  let updatedCount = 0;
  const byStableId = new Map<string, AlbumIndexRow>();
  for (const row of existing) {
    const sid = String(row.album_key ?? row.id ?? '').trim();
    if (sid) byStableId.set(sid, row);
  }

  for (const item of items) {
    const sid = String(item.id ?? '').trim();
    if (!sid) continue;
    const prior = byStableId.get(sid);
    if (!prior) continue;
    try {
      const resp = await libraryService.getAlbum(sid);
      const fresh = (resp as { album?: AlbumIndexRow })?.album;
      if (!fresh) continue;
      if (albumRowGridSlice(prior) === albumRowGridSlice(fresh)) continue;
      changed = true;
      updatedCount += 1;
      const key = albumIdentityKey(prior);
      albums = albums.map((a) => (albumIdentityKey(a) === key ? { ...a, ...fresh } : a));
      byStableId.set(sid, { ...prior, ...fresh });
    } catch {
      /* keep cached row */
    }
  }

  if (changed) {
    albums = dedupeAlbumIndexRows(albums).albums;
  }
  return { changed, albums, updatedCount };
}

async function runBackgroundIndexMerge(
  onMerge?: (snapshot: { albums: AlbumIndexRow[]; total: number; totalReleases: number; bytes: number }) => void,
): Promise<void> {
  const existing = indexAlbums.slice();
  await loadLocalAlbumIndex({
    sort: 'artist',
    mergeFirstChunk: existing.length > 0,
    existing,
    onChunk: (snapshot) => {
      onMerge?.({
        albums: snapshot.albums,
        total: snapshot.total,
        totalReleases: snapshot.totalReleases,
        bytes: snapshot.bytes,
      });
    },
  });
  refreshStoredGridFingerprint();
}

async function fetchCanonicalAlbumTotalFromServer(): Promise<number | null> {
  try {
    const resp = await callWithLibraryBusyRetry(() => libraryService.getAlbums({
      limit: 1,
      offset: 0,
      sort: 'artist',
    }));
    const total = Number(resp?.total_count ?? resp?.total);
    return Number.isFinite(total) && total >= 0 ? total : null;
  } catch (e) {
    if (isLibraryBusyScanningError(e)) return null;
    return null;
  }
}

/**
 * Warm-session background check — never blocks paint. Ignores catalogVersion-only
 * drift; reloads only when canonical album tile count or grid fingerprint changes.
 */
export async function revalidateLocalAlbumSession(options: {
  onMerge?: (snapshot: { albums: AlbumIndexRow[]; total: number; totalReleases: number; bytes: number }) => void;
} = {}): Promise<LocalAlbumSessionRevalidateResult> {
  if (!isLocalAlbumIndexWarm()) {
    return { status: 'unchanged', reason: 'cold' };
  }

  const meta = getLocalAlbumIndexSessionMeta();
  const started = performance.now();

  let statsResp: Awaited<ReturnType<typeof libraryService.getStats>> | null = null;
  try {
    statsResp = await callWithLibraryBusyRetry(() => libraryService.getStats());
  } catch (e) {
    if (isLibraryBusyScanningError(e)) {
      return { status: 'unchanged', reason: 'stats-busy' };
    }
    console.warn('[library-grid] session revalidate stats failed — keeping warm cache', e);
    return { status: 'unchanged', reason: 'stats-failed' };
  }

  const serverCanonical = await fetchCanonicalAlbumTotalFromServer();
  const countChanged = serverCanonical != null
    && meta.albumCount != null
    && serverCanonical !== meta.albumCount;

  if (countChanged) {
    console.log('[library-grid] grid background reload — canonical album count changed', {
      cached: meta.albumCount,
      server: serverCanonical,
      indexedReleases: statsResp?.albums ?? null,
    });
    await runBackgroundIndexMerge(options.onMerge);
    if (serverCanonical != null) setLocalAlbumIndexSessionMeta(null, serverCanonical);
    return { status: 'reloaded', reason: 'album-count' };
  }

  if (meta.gridFingerprint) {
    try {
      const probe = await libraryService.getAlbums({
        sort: 'artist',
        limit: ALBUM_INDEX_BULK_CAP,
        offset: 0,
      });
      const probeAlbumsRaw = (probe?.albums || []) as AlbumIndexRow[];
      const probeAlbums = applyChunkDedupe(probeAlbumsRaw).albums;
      if (gridFingerprintDrifted(meta.gridFingerprint, probeAlbums)) {
        console.log('[library-grid] grid background reload — tile fingerprint drift at stable count', {
          cached: meta.albumCount,
          server: serverCanonical,
          warmFingerprint: meta.gridFingerprint,
        });
        await runBackgroundIndexMerge(options.onMerge);
        return { status: 'reloaded', reason: 'fingerprint-drift' };
      }
    } catch (e) {
      if (isLibraryBusyScanningError(e)) {
        return { status: 'unchanged', reason: 'fingerprint-busy' };
      }
      console.warn('[library-grid] fingerprint drift probe failed — keeping warm cache', e);
    }
  }

  const sinceVersion = meta.catalogVersion;
  if (sinceVersion == null || !Number.isFinite(sinceVersion)) {
    console.log('[library-grid] library fetch end (session cache hit)', {
      ms: Math.round(performance.now() - started),
      albumCount: indexAlbums.length,
      fingerprint: meta.gridFingerprint,
      reason: 'no-version-baseline',
    });
    return { status: 'unchanged', reason: 'no-version-baseline' };
  }

  let diff: Awaited<ReturnType<typeof libraryService.getLibraryDiff>>;
  try {
    diff = await callWithLibraryBusyRetry(() => libraryService.getLibraryDiff(String(sinceVersion)));
  } catch (e) {
    if (isLibraryBusyScanningError(e)) {
      return { status: 'unchanged', reason: 'diff-busy' };
    }
    console.warn('[library-grid] session revalidate diff failed — keeping warm cache', e);
    return { status: 'unchanged', reason: 'diff-failed' };
  }

  const newVersion = Number(diff?.to_version ?? 0) || null;
  if (newVersion && newVersion !== sinceVersion) {
    setLocalAlbumIndexSessionMeta(newVersion, meta.albumCount);
  }

  if (diff?.full_refresh_required) {
    console.log('[library-grid] library fetch end (session cache hit)', {
      ms: Math.round(performance.now() - started),
      albumCount: indexAlbums.length,
      catalogVersion: newVersion ?? sinceVersion,
      fingerprint: meta.gridFingerprint,
      reason: 'full-resync-skipped-count-stable',
    });
    return { status: 'unchanged', reason: 'full-resync-skipped' };
  }

  const added = (diff?.added || []) as DiffEntityItem[];
  const updated = ((diff as { modified?: DiffEntityItem[] }).modified || []) as DiffEntityItem[];
  const removed = (diff?.removed || []) as DiffEntityItem[];

  const albumAdds = added.filter((i) => diffEntityType(i) === 'album');
  const albumRems = removed.filter((i) => diffEntityType(i) === 'album');
  if (albumAdds.length || albumRems.length) {
    console.log('[library-grid] grid background reload — album add/remove', {
      added: albumAdds.length,
      removed: albumRems.length,
    });
    await runBackgroundIndexMerge(options.onMerge);
    return { status: 'reloaded', reason: 'album-add-remove' };
  }

  const albumUpdates = updated.filter((i) => diffEntityType(i) === 'album');
  if (!albumUpdates.length) {
    console.log('[library-grid] library fetch end (session cache hit)', {
      ms: Math.round(performance.now() - started),
      albumCount: indexAlbums.length,
      catalogVersion: newVersion ?? sinceVersion,
      fingerprint: meta.gridFingerprint,
      reason: 'non-grid-catalog-changes',
    });
    return { status: 'unchanged', reason: 'non-grid-changes' };
  }

  const merged = await mergeGridRelevantAlbumUpdates(albumUpdates, indexAlbums);
  if (!merged.changed) {
    console.log('[library-grid] library fetch end (session cache hit)', {
      ms: Math.round(performance.now() - started),
      albumCount: indexAlbums.length,
      catalogVersion: newVersion ?? sinceVersion,
      albumUpdatesChecked: albumUpdates.length,
      fingerprint: meta.gridFingerprint,
      reason: 'album-updates-non-grid',
    });
    return { status: 'unchanged', reason: 'album-updates-non-grid' };
  }

  indexAlbums = merged.albums;
  indexBytes = estimatePayloadBytes(indexAlbums);
  refreshStoredGridFingerprint();
  options.onMerge?.({
    albums: indexAlbums,
    total: indexTotal,
    totalReleases: indexTotalReleases,
    bytes: indexBytes,
  });
  console.log('[library-grid] grid merged in place', {
    ms: Math.round(performance.now() - started),
    updatedTiles: merged.updatedCount,
    fingerprint: indexGridFingerprint,
  });
  return { status: 'merged', reason: 'album-grid-delta' };
}
